<template lang="pug">
  .layout
    y-dialog(fill-height fluid v-model="showModal" scrollable transition="fade-transition" persistent max-width="600px" :fullscreen="$vuetify.breakpoint.xsOnly")
      v-card
        v-card-title.px-5(:style="`background-color: ${$vuetify.theme.themes[$theme].basic600T16}`")
          v-row(no-gutters align="center")
            v-col.shrink
              v-btn.ml-n3(text icon @click="showModal = !showModal" height)
                v-icon(color="basic800" :size="24") mdi-close
            v-col.ml-3
              p.mb-0.h6.basic800--text(v-if="parentProduct") {{ $t('edit_bundle') }}
              p.mb-0.h6.basic800--text(v-else) {{ $t('new_bundle') }}

        v-card-text.pa-0
          v-form(ref="form")
            //- Loading state
            v-layout.my-6(v-if="isFetching")
              v-row.fill-height.ma-0(align='center' justify='center')
                v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
            //- Display state
            v-card.pa-4(v-else style="overflow-x: hidden;")
              //- Images
              .product-image.d-flex
                p.h6.basic1000--text {{ $t('product_images') }}
                v-spacer
                v-btn.button-AA-tiny(v-if="resizeImageFileUrl" color="textColorAccent" outlined x-small height="24" :style="{'background-color': $vuetify.theme.themes[$theme].infoT08}" @click="handleAddMoreImagesDialog()")
                  v-icon.mr-1(small) mdi-plus
                  | {{ $t('add_photo') }}
              v-card.mb-3(v-if="resizeImageFileUrl" flat)
                div(align="center")
                  v-img(:src="resizeImageFileUrl" aspect-ratio="1" width="170")
                    template(v-slot:placeholder)
                      v-row(class="fill-height ma-0" align="center" justify="center")
                        v-progress-circular(indeterminate color="grey")
              v-card.mb-3(v-else outlined height="170" :style="`background-color: ${$vuetify.theme.themes[$theme].primaryT08}`")
                div.fill-height
                  div.d-flex.justify-center.align-end(style="height: 70%")
                    div
                      div.mb-2(align="center")
                        eva-icon(name="image-outline" :fill="$vuetify.theme.themes[$theme].primaryT32" height="38" width="38")
                      div.file-layout.mb-4
                        v-btn.button-AA-tiny(color="textColorAccent" outlined x-small height="24" :style="{'background-color': $vuetify.theme.themes[$theme].infoT08}") upload
                        input.input-layout(type="file" @change="onFileChange" accept="image/*" style="width: 60px !important")
                  div.d-flex.justify-center.align-end(style="height: 30%")
                    div.mb-4
                      div.c1.basic600--text.text-center {{ $t('max_filesize_10mb') }}
                      div.c1.basic600--text.text-center {{ $t('recommended_image_size') }}

              .guide.mb-2.d-flex
                eva-icon.mr-2(name="info-outline" :fill="$vuetify.theme.themes[$theme].basic600" height="15" width="15" style="margin-top: -1px")
                div.c1.basic600--text {{ $t('multiple_images_are_enabled') }}

              //- Bundle details
              p.h6.basic1000--text.mt-5 {{ $t('details') }}

              .bundle-name
                p.mb-1.label.basic600--text.text-uppercase {{ $t('bundle_name') }}*
                v-text-field.mt-2(v-model="product.name" :placeholder="$t('write_bundle_name')" background-color="basic200" outlined dense height="40" persistent-hint :rules="rules.not_empty" counter :maxlength="145" hide-details="auto")

              .bundle-price
                p.mb-1.label.basic600--text.text-uppercase {{ $t('bundle_price') }}*
                v-text-field.mt-2(v-model="product.price" placeholder="0" background-color="basic200" outlined dense height="40" type="number" :rules="rules.has_quantity" :prefix="$currency.symbol" hide-details="auto")

              .bundle-saved-amount.my-3
                y-checkbox(v-model="displaySavedAmount" hide-details @change="displaySavedAmountChanged = true")
                  template(v-slot:label)
                    div.p2.basic600--text {{ $t('display_saved_amount') }}

              .description.pt-4
                p.mb-1.label.basic600--text.text-uppercase {{ $t('description') }}
                v-textarea.mt-2(v-model="product.description" :placeholder="$t('write_bundle_description')" outlined dense persistent-hint counter background-color="basic200" hide-details="auto")
                .guide.mb-2.d-flex
                  eva-icon.mr-2(name="info-outline" :fill="$vuetify.theme.themes[$theme].basic600" height="15" width="15")
                  div.c1.basic600--text {{ $t('for_optimized_design') }}

              v-row
                v-divider.basic300.mt-3

              //- Bundle items
              .bundle-items
                div.d-flex.align-center.pb-2.pt-4
                  p.h6.basic1000--text.mb-0 {{ $t('bundle_items') }}
                  v-spacer
                  v-btn.button-AA-tiny(v-if="parentProduct || changedBundle" color="textColorAccent" outlined x-small height="24" :style="{'background-color': $vuetify.theme.themes[$theme].infoT08}" @click="openSetBundleItemsDialog = true")
                    v-icon.mr-1(small) mdi-pencil-outline
                    | {{ $t('edit') }}
                template(v-if="parentProduct || changedBundle")
                  div.p1.basic700--text.py-2 {{ $t('customer_must_choose') }} {{ previewBundle.slots.length }} {{ $t('items_to_get_offer') }}
                  div.pb-4
                    bundle-list(v-for="(slot, slotIndex) in previewBundle.slots" :key="slotIndex" :products="slot.group.products")
                template(v-else)
                  p.p1.basic700--text {{ $t('select_items_and_products') }}

                  div.my-12.d-flex.justify-center(v-if="member.role === 'Admin'")
                    v-btn(color="textColorAccent" small style="background-color: rgba(51, 102, 255, 0.08)" outlined :ripple="false" @click="openSetBundleItemsDialog = true")
                      .info500--text.button-AA-small {{ $t('setup_bundle_items') }}

              v-row
                v-divider.basic300

              //- Rules and limits
              .bundle-rules-limits(v-if="member.role === 'Admin'")
                p.h6.basic1000--text.mt-5.mb-2 {{ $t('rules_and_limit') }}
                div.mb-4
                  v-row(no-gutters align="center")
                    v-col(cols="8")
                      p.p1.basic700--text.mb-0.pb-2 {{ $t('limit_available_bundles') }}
                    v-spacer
                    v-col
                      y-switch(v-model="product.inventory_enabled")
                    
                  div(v-if="product.inventory_enabled")
                    p.mb-1.label.basic600--text.text-uppercase {{ $t('available_bundles') }}
                    v-text-field.my-2(v-model.number="product.inventory_qty" placeholder="0" background-color="basic200" outlined dense height="40" type="number" :rules="rules.has_quantity" hide-details="auto")
                    div.mb-1.c1.basic600--text {{ $t('total_number_can_redeemed') }}

                  v-divider.basic300.my-6(v-if="product.inventory_enabled")

                div.mb-4
                  v-row(no-gutters align="center")
                    v-col(cols="8")
                      p.p1.basic700--text.mb-0.pb-2 {{ $t('limit_purchase_per_buyer') }}
                    v-spacer
                    v-col
                      y-switch(v-model="product.order_limit_enabled")
                  
                  div(v-if="product.order_limit_enabled")
                    p.mb-1.label.basic600--text.text-uppercase {{ $t('purchase_limit') }}
                    v-text-field.my-2(v-model.number="product.order_limit" placeholder="0" background-color="basic200" outlined dense height="40" type="number" :rules="rules.has_quantity" hide-details="auto")
                    div.mb-1.c1.basic600--text {{ $t('max_number_can_purchase') }}

              v-row
                v-divider.basic300.mt-3

              //- Store settings
              .yezza-store(v-if="Auth('root', 'store_features')")
                p.h6.basic1000--text.mt-5.mb-3 {{ $t('yezza_store_features') }}

                .product-category(v-if="categoryList.length > 0")
                  div.d-flex.align-end
                    p.mb-1.label.basic600--text.text-uppercase {{ $t('product_categories') }}
                    v-spacer
                    v-btn(icon small @click="category_keyboard = !category_keyboard")
                      v-icon(color="primary") {{ category_keyboard ? 'mdi-keyboard-outline' : 'mdi-keyboard-off-outline' }}
                  v-combobox.mt-2(v-model="product.categories" background-color="basic200" outlined dense :loading="memberSearchLoading" item-text="name" :search-input.sync="categorySearch" :items="categoryList" hide-selected hide-no-data chips placeholder="Select product category" multiple :menu-props="({ 'closeOnContentClick': false, 'maxHeight': category_keyboard ? '35%' : '45%' })" :type="category_keyboard ? 'text' : 'button'")
                    template(v-slot:selection="{ attrs, item, select, selected }")
                      v-chip.mb-1.s2(color="basic600" v-bind="attrs" outlined :style="`background-color: ${$vuetify.theme.themes[$theme].basic600T08}`" :input-value="selected" small close-icon="mdi-close" close @click.stop @click:close="removeSales(item)") {{ item.name }}
                    template(v-slot:item="{ index, item }")
                      v-layout
                        v-list-item-content
                          v-list-item-title(v-text="item.name")
                
                .meta-title
                  p.mb-1.label.basic600--text.text-uppercase {{ $t('meta_title_seo') }}
                  v-text-field.mt-2(v-model="product.meta_title" :placeholder="$t('enter_meta_title')" background-color="basic200" height="40" outlined dense :maxlength="100")

                .meta-description.mb-5
                  p.mb-1.label.basic600--text.text-uppercase {{ $t('meta_description_seo') }}
                  v-textarea.mt-2(v-model="product.meta_description" :placeholder="$t('enter_meta_description')" background-color="basic200" hide-details outlined dense :maxlength="200")
                  .guide.mt-3.d-flex
                    eva-icon.mr-2(name="info-outline" :fill="$vuetify.theme.themes[$theme].basic600" height="15" width="15")
                    div.c1.basic600--text {{ $t('note_meta_keyword') }} 
                      a.text-decoration-none.textColorAccent--text(href="https://webmasters.googleblog.com/2009/09/google-does-not-use-keywords-meta-tag.html" target="_blank") {{ $t('more_info_here') }}

        v-card-actions(v-if="!isFetching")
          v-spacer
          v-btn(color='white', text @click='showModal = false')
            .button-Aa-medium.primary400--text {{ $t('cancel') }}
          v-btn.mr-2(color='textColorAccent' depressed height=32 @click="save" :loading="isLoading" dark)
            .button-AA-medium.basic100--text {{ $t('save') }}

    .modals
      manage-product-images(v-if="openAddMoreImagesDialog" :open.sync="openAddMoreImagesDialog" :productId="parentProduct.id" :mainImage="resizeImageFileUrl" @updateImage="handleUpdateImage($event)")
      set-bundle-items(v-if="openSetBundleItemsDialog" :open.sync="openSetBundleItemsDialog" :parentSlots="changedBundle ? changedBundle.slots : originalBundle.slots" @updateSlot="handleUpdateSlot")

</template>

<script>
import clone from 'just-clone'
import { mapState, mapGetters, mapActions } from 'vuex'
import ManageProductImages from '@/components/dialogs/ManageProductImages.vue'
import SetBundleItems from '@/components/offers/dialogs/bundles/SetBundleItems.vue'
import BundleList from '@/components/offers/bundles/BundleList.vue'
import { resizeImage } from '@/utils'

export default {
  name: 'AddBundle',
  components: {
    ManageProductImages,
    SetBundleItems,
    BundleList
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    parentProduct: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isFetching: false,
      product: {
        name: null,
        price: null,
        description: '',
        inventory_enabled: false,
        inventory_qty: null,
        order_limit_enabled: false,
        order_limit: null,
        categories: [],
        members: [],
        meta_title: '',
        meta_description: ''
      },
      displaySavedAmount: false,
      displaySavedAmountChanged: false, // used during changed on edit mode and no modification on bundle itself
      openAddMoreImagesDialog: false,
      openSetBundleItemsDialog: false,
      resizeImageFileUrl: '',
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        number_not_empty: [v => v !== null || 'This field is required'],
        is_selected: [v => v !== null || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_items: [v => !!v || 'This field is required'],
        weight: [v => !!v || 'This field is required', v => v >= 0.1 || 'Weight should not be below 0.1 KG', v => v <= 60 || 'Weight should not be above 60 KG']
      },
      categoryList: [],
      metaTitle: null,
      metaDescription: null,
      category_keyboard: false,
      memberSearchLoading: false,
      categorySearch: null,
      isLoading: false,
      // bundle from fetching (default to 2 slots for new empty bundle)
      originalBundle: {
        id: null,
        slots: [
          // First slot
          {
            id: null,
            group: {
              type: 'bundle',
              products: []
            }
          },
          // Second slot
          {
            id: null,
            group: {
              type: 'bundle',
              products: []
            }
          }
        ]
      },
      // bundle if any changes from user
      changedBundle: null
    }
  },
  watch: {
    async categorySearch() {
      if (this.memberList.length != 0) {
        return
      }
      await this.startSearchMembers()
    }
  },
  async mounted() {
    try {
      this.isFetching = true
      const allFetching = [this.startSearchMembers()]
      if (this.parentProduct) {
        this.product = clone(this.parentProduct)
        this.product.price = this.product.price / this.$currency.multiplier
        if (this.product.image) {
          this.resizeImageFileUrl = this.product.image.file
        }
        allFetching.push(this.fetchBundleDetails())
      }
      await Promise.all([...allFetching])
      this.isFetching = false
    } catch (error) {
      console.log({ error })
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      this.showModal = false
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    ...mapState('user', ['member']),
    showModal: {
      get() {
        return this.open
      },
      set(val) {
        return this.$emit('update:open', val)
      }
    },
    authMember() {
      return this.$store.state.user.member
    },
    previewBundle() {
      if (this.changedBundle) {
        return this.changedBundle
      } else {
        return this.originalBundle
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchBundleDetails() {
      return this.axios.get(`/products/${this.parentProduct.id}/bundles`).then(resp => {
        this.originalBundle = resp.data[0]
        this.displaySavedAmount = resp.data[0].display_saved_amount
      })
    },
    handleAddMoreImagesDialog() {
      if (!this.parentProduct) {
        this.showMessage(['error', this.$t('error_message.please_add_product_first')])
        return
      }
      this.openAddMoreImagesDialog = true
    },
    handleUpdateImage(val) {
      this.resizeImageFileUrl = val[0]
      this.resizeImageFile = val[1]
    },
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      const resizedImage = await resizeImage(files[0], 'product_image.jpeg')
      this.resizeImageFileUrl = resizedImage.dataURL
      this.resizeImageFile = resizedImage.file
    },
    startSearchMembers() {
      this.memberSearchLoading = true
      return this.$axios
        .get(`/product_categories/?page_size=-1&company=${this.company.id}&ordering=-id`)
        .then(resp => {
          if (resp.data.results) {
            this.categoryList = resp.data.results
          }
        })
        .finally(() => (this.memberSearchLoading = false))
    },
    removeSales(item) {
      let index = this.product.categories
        .map(m => {
          return m.id
        })
        .indexOf(item.id)
      if (index >= 0) this.product.categories.splice(index, 1)
    },
    handleUpdateSlot(newSlot) {
      if (!this.changedBundle) {
        this.changedBundle = clone(this.originalBundle)
      }
      this.changedBundle.slots = clone(newSlot)
    },
    async save() {
      if (!this.$refs.form.validate()) {
        return
      }
      if (!this.parentProduct && !this.changedBundle) {
        this.showMessage(['error', this.$t('error_message.please_setup_bundle_items')])
        return
      }
      try {
        this.isLoading = true
        const resp = await this.saveProduct()
        const productId = resp.data.id
        let respSlots = null
        if (this.changedBundle) {
          respSlots = await this.saveBundleItems(productId)
        } else if (this.displaySavedAmountChanged) {
          // Occur on edit mode only
          await this.saveDisplaySavedAmountOnly(productId)
        }
        // Filter default properties
        const allowedProperties = ['id', 'status', 'name', 'description', 'price', 'image', 'inventory_enabled', 'order_limit_enabled', 'categories', 'members', 'meta_title', 'meta_description']
        const returnData = this.$options.filters.filterAllowedObjectProperties(resp.data, allowedProperties)
        // Custom properties
        if (resp.data.order_limit) {
          returnData.order_limit = resp.data.order_limit
        }
        if (resp.data.inventory_qty) {
          returnData.inventory_qty = resp.data.inventory_qty
        }
        if (resp.data.bundle) {
          returnData.total_items = resp.data.bundle.slots.length
          returnData.total_redeemed = resp.data.bundle.total_redeemed
        } else {
          returnData.total_items = respSlots.data.slots.length
          returnData.total_redeemed = 0
        }
        if (this.parentProduct) {
          this.$emit('edited', returnData)
        } else {
          this.$emit('added', returnData)
        }
        this.showMessage(['success', `Bundle ${this.parentProduct ? 'edited' : 'added'}`])
        this.showModal = false
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    saveProduct() {
      // Preparing data
      const memberIds = [this.authMember.id]
      let params = new FormData()
      params.set('company', this.company.id)
      params.set('name', this.product.name)
      params.set(
        'price',
        parseFloat(this.product.price)
          .toFixed(this.$currency.exponent)
          .replace('.', '')
      )
      params.set('description', this.product.description || '')
      params.set('meta_title', this.product.meta_title || '')
      params.set('meta_description', this.product.meta_description || '')
      if (this.product.inventory_enabled) {
        params.set('inventory_qty', this.product.inventory_qty)
        params.set('inventory_enabled', true)
      } else {
        params.set('inventory_enabled', false)
      }
      if (this.product.order_limit_enabled) {
        params.set('order_limit', this.product.order_limit)
        params.set('order_limit_enabled', true)
      } else {
        params.set('order_limit_enabled', false)
      }
      if (this.resizeImageFile) {
        params.append('image', this.resizeImageFile)
      }
      memberIds.forEach(mid => {
        params.set('members', mid)
      })
      this.product.categories.forEach(category => {
        params.append('categories', category.id)
      })
      if (!this.Auth('component', '/admin/offers#product_bundle')) {
        params.set('status', 'draft')
      }
      if (this.parentProduct) {
        return this.axios.put(`/products/${this.parentProduct.id}/`, params)
      } else {
        // Set default weight to 0 for first time create
        params.set('weight', 0)
        return this.axios.post(`/products/`, params)
      }
    },
    saveBundleItems(parentProductId) {
      // Preparing data
      const data = { ...this.changedBundle }
      // Change the display saved amount boolean accordingly
      data.display_saved_amount = this.displaySavedAmount
      data.slots = data.slots.map(slot => {
        const group = { id: slot.group.id, products: slot.group.products }
        group.products = group.products.map(product => {
          return {
            id: product.id,
            image: product.image,
            name: product.name,
            variants: product.variants.filter(variant => variant.enabled).map(variant => variant.id)
          }
        })
        return {
          id: slot.id,
          group
        }
      })
      if (this.originalBundle.id) {
        return this.axios.patch(`/products/${parentProductId}/bundles/${this.originalBundle.id}`, data)
      } else {
        return this.axios.post(`/products/${parentProductId}/bundles`, data)
      }
    },
    saveDisplaySavedAmountOnly(parentProductId) {
      // Preparing data
      const data = { display_saved_amount: this.displaySavedAmount }
      return this.axios.patch(`/products/${parentProductId}/bundles/${this.originalBundle.id}`, data)
    }
  }
}
</script>

<style scoped>
.container-image {
  border-style: solid;
  border-width: 1px;
  border-color: grey;
  border-radius: 2px;
}
.file-layout {
  position: relative;
}
.input-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.background-inventory {
  border-style: solid;
  border-width: 1px;
  border-color: grey;
  border-radius: 2px;
}
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>

<i18n>
{
  "en": {
    "edit_bundle": "Edit Bundle",
    "new_bundle": "New Bundle",
    "product_images": "Product Images",
    "max_filesize_10mb": "Max filesize 10 MB",
    "recommended_image_size": "Recommended image size 960x960 px",
    "multiple_images_are_enabled": "Multiple images are enabled only for Yezza Store",
    "details": "Details",
    "bundle_name": "Bundle Name",
    "write_bundle_name": "Write bundle name here",
    "bundle_price": "Bundle Price",
    "display_saved_amount": "Display saved amount on Yezza Store",
    "description": "Description",
    "write_bundle_description": "Write bundle description here",
    "bundle_items": "Bundle items",
    "edit": "Edit",
    "customer_must_choose": "Customer must choose",
    "items_to_get_offer": "items to get this offer.",
    "select_items_and_products": "Select items and products offered in this bundle",
    "setup_bundle_items": "Setup bundle items",
    "limit_available_bundles": "Limit available bundles",
    "available_bundles": "Available bundles",
    "total_number_can_redeemed": "Total number of times this bundle can be redeemed",
    "limit_purchase_per_buyer": "Limit purchase per buyer",
    "purchase_limit": "Purchase limit",
    "max_number_can_purchase": "Max number of bundle customer can purchase",
    "yezza_store_features": "Yezza Store Features",
    "product_categories": "Product Categories",
    "meta_title_seo": "Meta Title (SEO)",
    "enter_meta_title": "Enter meta title",
    "meta_description_seo": "Meta Description (SEO)",
    "enter_meta_description": "Enter meta description",
    "note_meta_keyword": "Note: Meta keywords is not supported.",
    "more_info_here": "More info here.",
    "rules_and_limit": "Rules and limits"
  },
  "id": {
    "edit_bundle": "Edit Paket",
    "new_bundle": "Paket Baru",
    "product_images": "Gambar produk",
    "max_filesize_10mb": "Ukuran file maksimal 10MB",
    "recommended_image_size": "Ukuran gambar yang disarankan 960x960px",
    "multiple_images_are_enabled": "Beberapa gambar hanya diizinkan untuk Yezza Store",
    "details": "Rincian",
    "bundle_name": "Nama Paket",
    "write_bundle_name": "Ketik nama paket di sini",
    "bundle_price": "Harga Paket",
    "display_saved_amount": "Tampilkan jumlah yang disimpan di Yezza Store",
    "description": "Keterangan",
    "write_bundle_description": "Ketik deskripsi paket di sini",
    "bundle_items": "Bundle/Paket item",
    "edit": "Edit",
    "customer_must_choose": "Pembeli harus memilih",
    "items_to_get_offer": "items untuk mendapatkan penawaran ini.",
    "select_items_and_products": "Pilih Item dan Produk yang ditawarkan dalam Bundle/Paket ini",
    "setup_bundle_items": "Atur Paket Item",
    "limit_available_bundles": "Batasi paket yang tersedia",
    "available_bundles": "Tersedia Bundle/Paket",
    "total_number_can_redeemed": "Total berapa kali Bundle/paket dapat ditukarkan",
    "limit_purchase_per_buyer": "Batasi pembelian per pembeli",
    "purchase_limit": "Purchase limit",
    "max_number_can_purchase": "Jumlah maksimum paket yang dapat dibeli pelanggan",
    "yezza_store_features": "Fitur Toko Yezza/Yezza Store",
    "product_categories": "Kategori Produk",
    "meta_title_seo": "Judul Meta (SEO)",
    "enter_meta_title": "",
    "meta_description_seo": "Deskripsi Meta (SEO)",
    "enter_meta_description": "Masukkan deskripsi meta",
    "note_meta_keyword": "Catatan: Kata kunci meta tidak didukung.",
    "more_info_here": "Info lebih lanjut di sini.",
    "rules_and_limit": "Aturan dan batasan"
  }
}
</i18n>